<template>
  <FlexCol gap-4 class="h-full min-h-0">
    <PageHeader :title="paramScreenName" :subtitle="screenSubtitle" :breadcrumb="hasBreadcrumb">
      <template #actions>
        <SelectMultiple v-if="allHeaders" v-model="selectHeaders" :items="allHeaders" item-value="field" item-text="title" class="w-[240px]" disableClear />

        <DropDown v-if="reports">
          <template #action>
            <Button light label="Relatórios" icon="fa-solid fa-file-invoice" class="" />
          </template>
          <template #default>
            <FlexCol gap-3 class="mt-2 w-[320px]">
              <ButtonExport
                v-for="(report, i) in reports"
                :label="report.label"
                :key="i"
                :api="report.api"
                :name="report.name"
                :disabled="!filters || filters.length < 1"
                v-model:filters="combinedFilters" />
            </FlexCol>
          </template>
        </DropDown>

        <TheFilter @filter="filterAll" @clear="clearStorage()" :filters="filterObject" v-model:filterValues="filters" />

        <Button primary label="Voltar" icon="fa-solid fa-chevron-left" class="" @click="router.back()" v-if="backButton" />

        <ButtonExport v-can="`${scope}.export`" v-show="!disableExport" :api="route" :name="paramScreenName" v-model:filters="combinedFilters" />

        <Button
          v-if="!disableRegister"
          id="addResourceBtn"
          :label="paramButtonNew"
          primary
          icon="fa-solid fa-circle-plus"
          class=""
          @click="navigate(paramRouteAdd)"
          v-can="`${scope}.create`" />

        <div v-else>
          <slot name="customNavigation"></slot>
        </div>

          <slot name="title-buttons" v-bind="{ filters, items, exportToXlsx, route, meta: extraData }"></slot>
      </template>
    </PageHeader>

    <slot name="middle-card" v-bind="{ filters, items, route, meta: extraData }"></slot>
    <div class="space-y-3" v-if="warnings.length > 0">
      <div
        v-for="warning in warnings"
        :key="warning"
        class="rounded-[10px] border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
        {{ warning }}
      </div>
    </div>
    <TheLoader v-if="loader" />

    <div class="grow min-h-0 relative" :style="tableAreaStyle">
      <Flex gap-3 items-stretch class="h-full min-h-0">
        <div class="grow min-h-0 overflow-hidden">
          <TableFull
            :headers="computedHeaders"
            :items="visibleItems"
            v-model="tableControl"
            @selectedItem="handleSelectItem"
            @doubleClick="handleDoubleClick"
            :dense="tableDensity === 'dense'"
            :ultraDense="tableDensity === 'ultra'"
            stickyActions>
            <template v-slot:actions="{ item }">
              <div class="flex gap-2">
                <slot name="actions" v-bind="{ item }"></slot>
                <div class="w-4">
                  <TheToolTip label="Editar">
                    <TableIconButton v-if="!disableEdit" icon="fa-pen" @click="handleEdit(item)" v-can="`${scope}.edit`" />
                  </TheToolTip>
                </div>
                <div class="w-4">
                  <TheToolTip label="Excluir" v-if="!disableDelete">
                    <TableIconButton icon="fa-trash" v-if="props.canDelete(item)" @click.stop="removeConfirm(item, $event)" v-can="`${scope}.delete`" />
                  </TheToolTip>
                </div>
              </div>
            </template>
            <template v-for="head in computedHeaders.filter((head) => head.field !== 'actions')" #[`${head.field}`]="{ item }">
              <slot :name="`${head.field}`" v-bind="{ item, head }"></slot>
            </template>
          </TableFull>
        </div>
        <div class="w-[640px] shrink-0" v-if="selected && selectable"></div>
      </Flex>
      <Card class="w-[640px] absolute top-0 right-0 bottom-2 overflow-y-scroll" v-if="selected && selectable">
        <FlexCol gap-3>
          <Flex items-center justify-between class="pb-3 border-b">
            <slot name="card-title" v-bind="{ item: selected }">
              <span>{{ paramTitleCard }}</span>
            </slot>
            <Flex items-center justify-end gap-3>
              <IconButton icon="fa-solid fa-pen" @click="handleEdit(selected)" />
              <IconButton icon="fa-solid fa-x" @click="selected = null" />
            </Flex>
          </Flex>
          <slot name="card" v-bind="{ item: selected }"></slot>
        </FlexCol>
      </Card>
    </div>

    <DeleteConfirmModal :title="deleteModalTitle" :itemName="choosed?.name" v-model="dialogDelete" @confirm="removeAcept()">
      Você deseja excluir <b v-if="choosed?.name">"{{ choosed.name }}"</b><span v-else>este registro</span>?
    </DeleteConfirmModal>
  </FlexCol>
</template>

<!-- 
=============== Slots
Slots para Tabela : Nome do "Field" no header
Slot para Ação : actions

-->
<script setup>
import { ref, computed, onMounted, watch, onBeforeMount } from 'vue'
import { get, remove } from '@/crud.js'
import { useExport } from '@/composables/useExport'
import { useRouter, useRoute } from 'vue-router'

const router = useRouter()
const routeInfo = useRoute()
const { exportToXlsx, exportLoader } = useExport()

// Daqui pra cima é Genérico
const props = defineProps({
  modelValue: Object,
  selectable: Boolean,
  disableDelete: Boolean,
  disableEdit: { type: Boolean, default: false },
  refresh: Boolean,
  backButton: Boolean,
  disableRegister: { type: Boolean, default: false },
  disableExport: { type: Boolean, default: false },
  protocolRedirect: { type: Boolean, default: false },
  canDelete: { type: Function, default: () => true },
})

const emits = defineEmits('update:modelValue', 'update:refresh', 'removedItem')

const scope = ref(props.modelValue.scope ?? null)

const paramScreenName = computed(() => props.modelValue.screenName ?? '')
const screenSubtitle = computed(() => props.modelValue.screenSubtitle ?? 'Gerencie os registros cadastrados.')
const paramButtonNew = ref(props.modelValue.buttonNew ?? '')
const paramTitleModalDelete = ref(props.modelValue.titleModalDelete ?? '')
const paramTitleCard = ref(props.modelValue.titleCard ?? '')

const route = ref(props.modelValue.api ?? '')
const paramRouteAdd = ref(props.modelValue.routeAdd ?? '')
const paramRouteEdit = ref(props.modelValue.routeEdit ?? '')

const reports = ref(props.modelValue.reports)
const tableAreaMinHeight = computed(() => props.modelValue.tableAreaMinHeight ?? null)
const tableDensity = computed(() => props.modelValue.tableDensity ?? 'dense')

const filterObject = ref(props.modelValue.filters)
const filters = ref(props.modelValue.filter ?? [])

const filterPerene = ref(props.modelValue.filterPerene)

const headers = ref(props.modelValue.headers)
const allHeaders = ref(props.modelValue.allHeaders)
const selectHeaders = ref([...headers.value.map((el) => el.field)])
const headersLocal = ref([])
headersLocal.value = [
  ...props.modelValue.headers,
  {
    title: 'Ações',
    field: 'actions',
    itemClass: 'w-[120px] text-center ',
    headerClass: 'justify-center',
    disableOrder: true,
  },
]

//  Controle de Tabela
const tableControl = ref({
  page: 1,
  total: 0,
  limit: props.modelValue.limit ?? 50,
  pages: 0,
  order: props.modelValue.order || [],
})

const choosed = ref(null)
const selected = ref(null)
const items = ref([])
const loader = ref(false)
const dialogDelete = ref(false)
const extraData = ref({})
const warnings = computed(() => (Array.isArray(extraData.value?.warnings) ? extraData.value.warnings : []))
const visibleItems = computed(() => {
  const filterFn = props.modelValue?.clientFilterFn
  if (typeof filterFn !== 'function') {
    return items.value
  }

  try {
    return items.value.filter((item, index) => filterFn(item, { index, items: items.value }))
  } catch (error) {
    console.error('Erro ao aplicar filtro local da tela:', error)
    return items.value
  }
})

const isLoading = ref(false)
const initializing = ref(true)
// Computados
const computedHeaders = computed(() => {
  if (!allHeaders.value) {
    return headersLocal.value
  }
  const data = []
  const fixed = headers.value.filter((el) => el.fixed)
  const selectableHeaders = allHeaders.value.filter((el) => selectHeaders.value.includes(el.field))
  data.push(...fixed)
  data.push(...selectableHeaders)
  data.push({
    title: 'Ações',
    field: 'actions',
    itemClass: 'w-[120px] text-center ',
    headerClass: 'justify-center',
    disableOrder: true,
  })
  return data
})

const combinedFilters = computed(() => {
  const combined = [...(filters.value || [])]
  if (filterPerene.value && filterPerene.value.length > 0) {
    combined.push(...filterPerene.value)
  }
  return combined
})

const deleteModalTitle = computed(() => {
  const title = paramTitleModalDelete.value || 'Excluir Registro'
  return title.replace(/^Remover\b/i, 'Excluir')
})

const hasBreadcrumb = computed(() => Array.isArray(routeInfo.meta?.breadcrumb) && routeInfo.meta.breadcrumb.length > 0)
const tableAreaStyle = computed(() => {
  if (!tableAreaMinHeight.value) return {}
  return {
    minHeight: tableAreaMinHeight.value,
  }
})

const STORAGE_KEY = 'manage-shell-control'

const clearStorage = () => {
  const allStates = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}')
  allStates[routeInfo.name] = null
  localStorage.setItem(STORAGE_KEY, JSON.stringify(allStates))
  filters.value = null
  loadItems(5)
}

const saveState = () => {
  if (!routeInfo.name) return

  try {
    const allStates = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}')
    allStates[routeInfo.name] = {
      filters: filters.value,
      tableControl: {
        page: tableControl.value.page,
        limit: tableControl.value.limit,
        order: tableControl.value.order,
      },
      selectHeaders: selectHeaders.value,
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(allStates))
  } catch (error) {
    console.error('Erro ao salvar estado no localStorage:', error)
  }
}

const uniqueByKey = (array, key) => {
  const seen = new Set()
  return array.filter((item) => {
    if (seen.has(item[key])) return false
    seen.add(item[key])
    return true
  })
}

const loadState = () => {
  if (!routeInfo.name) return false

  try {
    const allStates = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}')
    const state = allStates[routeInfo.name]

    if (!state) return false
    if (state.filters) {
      if (!filters.value || filters.value.length === 0) {
        filters.value = state.filters
      } else {
        filters.value.push(...state.filters)
        filters.value = uniqueByKey(filters.value, 'column')
      }
    }

    if (state.tableControl) {
      if (state.tableControl.page) tableControl.value.page = state.tableControl.page
      if (state.tableControl.limit && props.modelValue.persistLimit !== false) tableControl.value.limit = state.tableControl.limit
      if (state.tableControl.order) tableControl.value.order = state.tableControl.order
    }
    if (state.selectHeaders && allHeaders.value) {
      selectHeaders.value = state.selectHeaders
    }

    return true
  } catch (error) {
    console.error(error)
    return false
  }
}

// Metódos
const navigate = (routeName, paramObj) => {
  let route = {
    name: routeName,
    params: { ...(routeInfo.params || {}) },
  }
  if (paramObj) {
    route.params = { ...route.params, ...paramObj }
  }

  router.push(route)
}

const loadItems = async () => {
  if (isLoading.value) return // evita duplicar
  isLoading.value = true

  try {
    loader.value = true

    const query = {
      params: {
        page: tableControl.value.page,
        limit: tableControl.value.limit,
        order: tableControl.value.order,
        filters: [],
      },
    }

    if (filters.value?.length) query.params.filters.push(...filters.value)
    if (filterPerene.value?.length) query.params.filters.push(...filterPerene.value)

    const raw = await get(route.value, query)

    items.value = raw.data
    extraData.value = raw || {}
    tableControl.value.pages = Math.ceil(raw.total / tableControl.value.limit)
    tableControl.value.total = raw.total
  } catch (error) {
    console.error(error.message)
  } finally {
    loader.value = false
    isLoading.value = false
  }
}

const removeConfirm = (item, event) => {
  if (event) {
    event.preventDefault()
    event.stopPropagation()
  }
  if (!props.canDelete(item)) return
  choosed.value = item
  dialogDelete.value = true
}

const removeAcept = async () => {
  dialogDelete.value = false
  selected.value = null
  await remove(route.value, choosed.value.id, 'recurso')
  await loadItems(1)
  emits('removedItem')
}

const filterAll = async (event) => {
  filters.value = event && event.params ? event.params : []
  tableControl.value.page = 1
  saveState()
  await loadItems(2)
}

// Funções de Ação
const handleSelectItem = (item) => {
  selected.value = item
}

const handleEdit = (item) => {
  if (props.protocolRedirect) {
    return navigate(paramRouteEdit.value, { protocolo: item.protocolo }, true)
  }
  navigate(paramRouteEdit.value, { id: item.id }, true)
}

const handleDoubleClick = (item) => {
  if (props.protocolRedirect) {
    const resolved = router.resolve({
      name: paramRouteEdit.value,
      params: { protocolo: item.protocolo },
    })
    window.open(resolved.href, '_blank')
    return
  }
  handleEdit(item)
}

// Watchers

watch(
  () => tableControl.value,
  async () => {
    if (initializing.value) return
    await loadItems(3)
    saveState()
  },
  { deep: true },
)

watch(
  () => props.refresh,
  async (newVal) => {
    if (!newVal) return
    if (initializing.value) return
    await loadItems(4)
    emits('update:refresh', false)
  },
)

watch(
  () => selectHeaders.value,
  () => {
    saveState()
  },
  { deep: true },
)

watch(exportLoader, (newVal) => {
  loader.value = newVal
})

onBeforeMount(() => {
  loadState()
})

onMounted(async () => {
  await loadItems()
  initializing.value = false
})
</script>
